import React, {useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo, useCallback} from 'react';
import { useMsal } from '@azure/msal-react';
import {Player, PlayerEvent} from 'bitmovin-player';
import { UIFactory } from 'bitmovin-player/bitmovinplayer-ui';
import 'bitmovin-player/bitmovinplayer-ui.css';
import configData from "../../../../../config.json";
import Loading from '../../../../../components/Loading';
import AlertMessage from '../../../../../components/controls/AlertMessage';

// Frontend/backend dependency (see openspec change aa-compliance-site-remediation-certifications,
// design.md Decision 5 and specs/certification-media-accessibility/spec.md): no content response
// currently returned by the API includes caption, transcript, audio-description, or
// approved-equivalent-media metadata. The fields read below (CaptionUrl, TranscriptUrl,
// AudioDescriptionUrl, AlternativeDocumentUrl, EquivalentMediaApproved, etc.) are read
// defensively so that whenever such metadata becomes available from the backend/content
// authoring pipeline, it is surfaced automatically without a further frontend change. Until
// then, these branches remain inert and the UI does not fabricate alternatives that do not
// exist in current frontend data.
const getMediaAlternativeLinks = (source) => {
  if (!source) return [];
  const links = [];
  if (source.TranscriptUrl) {
    links.push({
      key: 'transcript',
      href: source.TranscriptUrl,
      label: source.TranscriptLabel || 'Video transcript',
    });
  }
  if (source.AudioDescriptionUrl) {
    links.push({
      key: 'audio-description',
      href: source.AudioDescriptionUrl,
      label: source.AudioDescriptionLabel || 'Audio-described version',
    });
  }
  if (source.AlternativeDocumentUrl) {
    links.push({
      key: 'alternative-document',
      href: source.AlternativeDocumentUrl,
      label: source.AlternativeDocumentLabel || 'Alternative document',
    });
  }
  return links;
};

const BitmovinPlayer = forwardRef(({
  content,
  onEnded,
  onPause,
  currentTimeStamp,
  startAtEnd,
  autoplay = false,
  mediaTitle = 'Video player',
  onRetry,
  onReturn,
  returnLabel = 'Return',
}, ref) => {

  // Override console.error to suppress isCasting errors
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const message = args[0];
      if (typeof message === 'string' && message.includes('isCasting')) {
        return; // Suppress isCasting errors
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      // Restore original console.error on cleanup
      console.error = originalConsoleError;
    };
  }, []);

  const [player, setPlayer] = useState(null);
  const [playerSource, setPlayerSource] = useState(null);
  const [isPlaybackVisible, setIsPlaybackVisible] = useState(false);
  // Known immediately from content metadata, before any network request.
  const [invalidSource, setInvalidSource] = useState(false);
  // Raised when the player attempts to load a valid-looking source and fails.
  const [loadError, setLoadError] = useState(false);
  const [showLoadError, setShowLoadError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const { instance, accounts } = useMsal();

  const playerDiv = useRef();
  const playerRef = useRef(null);
  const keepaliveIntervalRef = useRef(null);
  const initialSeekRef = useRef(currentTimeStamp);
  initialSeekRef.current = currentTimeStamp;
  const startAtEndRef = useRef(startAtEnd);
  startAtEndRef.current = startAtEnd;

  const mediaAlternativeLinks = useMemo(() => getMediaAlternativeLinks(content), [content]);
  // See dependency note above `getMediaAlternativeLinks`: this flag is only ever true when a
  // future backend/content response explicitly marks an alternative as an approved equivalent.
  const equivalentMediaApproved = Boolean(content?.EquivalentMediaApproved) && mediaAlternativeLinks.length > 0;

  const playerConfig = useMemo(() => ({
    key: configData.BITMOVIN_PLAYER_KEY,
    ui: false,
    playback: {autoplay: Boolean(autoplay)}
  }), [autoplay]);

  const handleRetry = useCallback(() => {
    setLoadError(false);
    setShowLoadError(false);
    setRetryToken((token) => token + 1);
    onRetry?.();
  }, [onRetry]);

  const handleAcknowledgeEquivalentMedia = useCallback(() => {
    if (!onEnded) return;
    onEnded();
  }, [onEnded]);

  const handleOnEnded = useCallback(() => {
    if (!onEnded) return;
    onEnded();
  }, [onEnded]);

  const handleOnPause = useCallback(() => {
    const currentPlayer = playerRef.current;
    if (currentPlayer) {
      try {
        onPause?.(currentPlayer.getCurrentTime());
      } catch (error) {
        console.warn('Player getCurrentTime error in handleOnPause:', error.message);
      }
    }
  }, [onPause]);

  useEffect(() => {
    setIsPlaybackVisible(false);
    setInvalidSource(false);
    setLoadError(false);
    setShowLoadError(false);

    if (!content) {
      // No content to play — clear any previous source so the old player is
      // destroyed rather than left running against stale state.
      setPlayerSource(null);
      return;
    }

    // Validate source before setting it. Some content records only carry a plain
    // FileUrl (progressive download) rather than Dash/Hls streaming manifests.
    const hasValidStream = content.DashUrl || content.HlsUrl || content.FileUrl;
    if (!hasValidStream) {
      console.warn('Invalid video source: No Dash, Hls, or File URL provided', content);
      setInvalidSource(true);
      setPlayerSource(null);
      return;
    }

    setPlayerSource(() => ({
      dash: content.DashUrl,
      hls:  content.HlsUrl,
      ...(content.DashUrl || content.HlsUrl ? {} : { progressive: content.FileUrl }),
      poster: content.thumbUrl,
      // Present only when current content metadata supplies a caption track; see the
      // frontend/backend dependency note above `getMediaAlternativeLinks`.
      ...(content.CaptionUrl
        ? {
            subtitleTracks: [
              {
                id: 'captions',
                url: content.CaptionUrl,
                label: content.CaptionLabel || 'Captions',
                lang: content.CaptionLanguage || 'en',
              },
            ],
          }
        : {}),
    }));
  }, [content]);

  useEffect(() => {
    const currentPlayer = playerRef.current;
    if(currentPlayer) {
      try {
        currentPlayer.on(PlayerEvent.Paused, handleOnPause);
        currentPlayer.on(PlayerEvent.PlaybackFinished, handleOnEnded);
      } catch (error) {
        console.warn('Player event listener setup error:', error.message);
        return;
      }

      return () => {
        try {
          currentPlayer.off(PlayerEvent.Paused, handleOnPause);
          currentPlayer.off(PlayerEvent.PlaybackFinished, handleOnEnded);
        } catch (error) {
          // Player already destroyed or in invalid state
          console.warn('Player event cleanup error (already destroyed):', error.message);
        }
      };
    }
  }, [player, handleOnPause, handleOnEnded]);

  useEffect(() => {
    const currentPlayer = playerRef.current;
    if (!currentPlayer) return;

    const KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000; // Fire token check every 4 minutes, since MSAL checks token validity and only gets new token when clsoe to expiration.

    const doKeepalive = async () => {
      try {
        await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [
            `https://${configData.AUTH.ADB2C_TENANT_NAME}.onmicrosoft.com/core-api/core-api.access`,
          ],
        });
      } catch (err) {
        console.log('[keepalive] acquireTokenSilent error (swallowed):', err?.message);
      }
    };

    const handlePlaying = () => {
      clearInterval(keepaliveIntervalRef.current);
      doKeepalive();
      keepaliveIntervalRef.current = setInterval(doKeepalive, KEEPALIVE_INTERVAL_MS);
    };

    const handleStop = () => {
      clearInterval(keepaliveIntervalRef.current);
      keepaliveIntervalRef.current = null;
    };

    try {
      currentPlayer.on(PlayerEvent.Playing, handlePlaying);
      currentPlayer.on(PlayerEvent.Paused, handleStop);
      currentPlayer.on(PlayerEvent.PlaybackFinished, handleStop);
    } catch (error) {
      console.warn('Keepalive event listener setup error:', error.message);
      return;
    }

    return () => {
      clearInterval(keepaliveIntervalRef.current);
      keepaliveIntervalRef.current = null;
      try {
        currentPlayer.off(PlayerEvent.Playing, handlePlaying);
        currentPlayer.off(PlayerEvent.Paused, handleStop);
        currentPlayer.off(PlayerEvent.PlaybackFinished, handleStop);
      } catch {
        // Player already destroyed
      }
    };
  }, [player, instance, accounts]);

  useEffect(() => {
    let cancelled = false;
    let loadTimeoutId;

    async function setupPlayer() {
      if (!playerSource) return;

      setIsPlaybackVisible(false);
      const playerInstance = new Player(playerDiv.current, playerConfig);
      UIFactory.buildDefaultUI(playerInstance);
      // Track the instance immediately so cleanup destroys it even when load()
      // rejects or this run is superseded before it completes.
      playerRef.current = playerInstance;

      // Bound how long a hung load() can block the user before an actionable
      // error is announced.
      loadTimeoutId = setTimeout(() => {
        if (!cancelled) {
          setLoadError(true);
        }
      }, 15000);

      try {
        await playerInstance.load(playerSource);
        if (cancelled) return;
        clearTimeout(loadTimeoutId);
        if (startAtEndRef.current) {
          const duration = playerInstance.getDuration();
          if (duration && isFinite(duration)) {
            playerInstance.seek(duration);
          }
        } else {
          playerInstance.seek(initialSeekRef.current);
        }
        setPlayer(playerInstance);
        // Reveal the player (and its explicit play control) as soon as the source is ready,
        // instead of waiting for a `Playing` event that will never fire without autoplay.
        setIsPlaybackVisible(true);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading player source:', error);
        setLoadError(true);

        // Handle specific error codes
        if (error.code === 1201) { // SOURCE_INVALID
          console.warn('Source invalid - no usable streams found');
        }
      }
    }

    setupPlayer();

    return () => {
      cancelled = true;
      clearTimeout(loadTimeoutId);
      const currentPlayer = playerRef.current;
      if (currentPlayer != null) {
        setPlayer(null);
        playerRef.current = null;
        try {
          currentPlayer.destroy();
        } catch (error) {
          console.warn('Player destroy error (already destroyed):', error.message);
        }
      }
    };
  }, [playerSource, playerConfig, retryToken]);

  // Bound how long a load failure is silently retried/pending before an accessible,
  // actionable error is announced, so loading cannot block the user indefinitely.
  useEffect(() => {
    if (loadError) {
      const timer = setTimeout(() => {
        setShowLoadError(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
    setShowLoadError(false);
    return undefined;
  }, [loadError]);

  const hasVisibleError = invalidSource || (loadError && showLoadError);

  // Expose player methods through ref
  useImperativeHandle(ref, () => ({
    pause: () => {
      const currentPlayer = playerRef.current;
      if (currentPlayer) {
        try {
          if (currentPlayer.isPlaying()) {
            currentPlayer.pause();
            onPause?.(currentPlayer.getCurrentTime());
          }
        } catch (error) {
          console.warn('Player pause error:', error.message);
        }
      }
    },
    getCurrentTime: () => {
      const currentPlayer = playerRef.current;
      if (currentPlayer) {
        try {
          return currentPlayer.getCurrentTime();
        } catch (error) {
          console.warn('Player getCurrentTime error:', error.message);
          return 0;
        }
      }
      return 0;
    },
    play: () => {
      const currentPlayer = playerRef.current;
      if (currentPlayer) {
        try {
          currentPlayer.play();
        } catch (error) {
          console.warn('Player play error:', error.message);
        }
      }
    },
    getPlayer: () => playerRef.current
  }));

  return (
    <div className="bitmovin-player-container" role="group" aria-label={mediaTitle}>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {!isPlaybackVisible && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff',
              zIndex: 1
            }}
          >
            {hasVisibleError ? (
              <AlertMessage className="bitmovin-player-error" style={{ textAlign: 'center', color: '#666' }}>
                <div>Unable to load video</div>
                <div style={{ fontSize: '0.9em', marginTop: '0.5em' }}>
                  The video source is invalid or unavailable.
                </div>
                <div className="bitmovin-player-error-actions" style={{ marginTop: '0.75em', display: 'flex', gap: '0.5em', justifyContent: 'center' }}>
                  <button type="button" onClick={handleRetry}>
                    Retry
                  </button>
                  {onReturn && (
                    <button type="button" onClick={onReturn}>
                      {returnLabel}
                    </button>
                  )}
                </div>
              </AlertMessage>
            ) : (
              <Loading />
            )}
          </div>
        )}
        <div
          id='player'
          ref={playerDiv}
          style={{ visibility: isPlaybackVisible && !hasVisibleError ? 'visible' : 'hidden' }}
        />
      </div>
      {mediaAlternativeLinks.length > 0 && (
        <ul className="bitmovin-player-alternatives" aria-label="Additional accessible media resources">
          {mediaAlternativeLinks.map((link) => (
            <li key={link.key}>
              <a href={link.href} target="_blank" rel="noopener noreferrer">
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      )}
      {equivalentMediaApproved && (
        <div className="bitmovin-player-equivalent-media">
          <p>An accessible alternative for this video is available above.</p>
          <button type="button" onClick={handleAcknowledgeEquivalentMedia}>
            I have reviewed the accessible alternative
          </button>
        </div>
      )}
    </div>
  );
});

export default BitmovinPlayer;